import React, {useEffect, useState} from "react";
import {
    Card,
    Statistic,
    Typography,
    theme,
    Flex,
    Tooltip
} from "antd";
import {
    ArrowUpOutlined,
    ArrowDownOutlined,
    UserAddOutlined,
    TeamOutlined,
    UsergroupAddOutlined,
    UserOutlined,
    DollarOutlined,
    ShoppingCartOutlined,
    TrophyOutlined,
    StarOutlined,
    MinusOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import {useWidgetStatistics} from "../useSatistics";
import ErrorPage from "../../../shared/components/ErrorPage";
import IsLoading from "../../../shared/components/IsLoading";

const {Text} = Typography;

const getWidgetConfig = (type, widgetData) => {
    const configs = {
        newUser: {
            title: "신규 사용자",
            shortTitle: "NEW USERS",
            isMoney: false,
            link: `어제: ${widgetData.sub?.toLocaleString()}명`,
            type: 'dnu',
            icon: <UserAddOutlined/>
        },
        dau: {
            title: "일간 활성 사용자",
            shortTitle: "DAU",
            link: `어제: ${widgetData.sub?.toLocaleString()}명`,
            type: 'dau',
            icon: <TeamOutlined/>
        },
        mau: {
            title: `월간 활성 사용자 (${dayjs().format("MMMM")})`,
            shortTitle: `MAU (${dayjs().format("MMM")})`,
            link: `지난달 (${dayjs().add(-1, 'month').format("MMM")}): ${widgetData.sub?.toLocaleString()}명`,
            type: 'mau',
            icon: <UsergroupAddOutlined/>
        },
        total: {
            title: "총 사용자",
            shortTitle: "TOTAL USERS",
            link: `탈퇴 사용자: ${widgetData.sub?.toLocaleString()}명`,
            type: 'total',
            icon: <UserOutlined/>
        },
        revenue: {
            title: "수익",
            shortTitle: "REVENUE",
            isMoney: true,
            link: `어제: $${widgetData.sub?.toFixed(2)}`,
            type: 'revenue',
            icon: <DollarOutlined/>
        },
        pu: {
            title: "결제 사용자",
            shortTitle: "PU",
            link: `어제: ${widgetData.sub?.toLocaleString()}명`,
            type: 'pu',
            icon: <ShoppingCartOutlined/>
        },
        arpu: {
            title: "사용자당 평균 수익",
            shortTitle: "ARPU",
            isMoney: true,
            link: `어제: $${widgetData.sub?.toFixed(2)}`,
            type: 'arpu',
            icon: <TrophyOutlined/>
        },
        arppu: {
            title: "결제 사용자당 평균 수익",
            shortTitle: "ARPPU",
            isMoney: true,
            link: `어제: $${widgetData.sub?.toFixed(2)}`,
            type: 'arppu',
            icon: <StarOutlined/>
        },
        default: {}
    };
    return configs[type];
};


const Widget = ({type, currentType, handleChartChange}) => {
    const {token} = theme.useToken();
    const {data: widgetData, isLoading, error} = useWidgetStatistics(type);
    const [configData, setConfigData] = useState(!isLoading && getWidgetConfig(type, widgetData));
    const [isSelected] = useState(currentType === configData.type);
    const [isClickable] = useState(type !== 'total');

    useEffect(() => {
        if (!isLoading) {
            setConfigData(getWidgetConfig(type, widgetData));
        }
    }, [isLoading, type, widgetData]);

    const handleClick = () => {
        if (isClickable) {
            handleChartChange(configData.title, configData.type);
        }
    };

    const formatValue = () => {
        if (configData?.isMoney) {
            return widgetData?.main?.toFixed(2);
        }
        return widgetData?.main;
    };

    const getTrendIcon = () => {
        if (type === 'total') return null;

        if (widgetData?.diff > 0) {
            return <ArrowUpOutlined style={{color: '#477fea'}}/>;
        } else if (widgetData?.diff < 0) {
            return <ArrowDownOutlined style={{color: '#f5222d'}}/>;
        } else {
            return <MinusOutlined style={{color: token.colorTextSecondary}}/>;
        }
    };

    const getTrendColor = () => {
        if (widgetData?.diff > 0) return '#477fea';
        if (widgetData?.diff < 0) return '#f5222d';
        return token.colorTextSecondary;
    };

    const getCardStyle = () => {
        const baseStyle = {
            borderRadius: '16px',
            transition: 'all 0.3s ease',
            cursor: isClickable ? 'pointer' : 'default',
            height: '140px',
            border: 'none',
            position: 'relative',
            overflow: 'hidden'
        };

        if (isSelected) {
            return {
                ...baseStyle,
                background: 'linear-gradient(135deg, #fafbfc, #f5f5f5)',
                boxShadow: `0 4px 20px rgba(140, 140, 140, 0.15), 0 0 0 2px rgba(140, 140, 140, 0.2)`,
                transform: 'translateY(-2px)'
            };
        }

        if (isClickable) {
            return {
                ...baseStyle,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                '&:hover': {
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    transform: 'translateY(-1px)'
                }
            };
        }

        return {
            ...baseStyle,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        };
    };

    if (error) return <ErrorPage error={error}/>;

    return (
        <Tooltip
            title={isClickable ? `클릭하여 ${configData.title} 차트 보기` : configData.title}
            placement="top"
        >
            <Card
                loading={isLoading}
                style={getCardStyle()}
                styles={{
                    body: {
                        padding: '20px',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                    }
                }}
                onClick={handleClick}
                hoverable={isClickable}
            >
                {/* 상단 섹션: 아이콘과 제목을 한 줄에 */}
                <Flex justify="space-between" align="flex-start">
                    <Flex align="center" gap={10}>
                        <div style={{
                            background: '#a0b2f1',
                            borderRadius: '8px',
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            boxShadow: '0 2px 6px rgba(140, 140, 140, 0.25)'
                        }}>
                            <span style={{
                                color: '#fff',
                                fontSize: '16px'
                            }}>
                                {configData.icon}
                            </span>
                        </div>

                        <Text
                            type="secondary"
                            style={{
                                fontSize: '13px',
                                fontWeight: 600,
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {configData.shortTitle}
                        </Text>
                    </Flex>

                    {/* 트렌드 표시 */}
                    {type !== 'total' && (
                        <div style={{
                            background: token.colorBgContainer,
                            borderRadius: '12px',
                            padding: '4px 8px',
                            border: `1px solid ${token.colorBorderSecondary}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}>
                            {getTrendIcon()}
                            <Text
                                style={{
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    color: getTrendColor()
                                }}
                            >
                                {Math.abs(widgetData?.diff || 0).toFixed(1)}%
                            </Text>
                        </div>
                    )}
                </Flex>

                {/* 메인 수치 */}
                <div style={{margin: '16px 0 8px 0'}}>
                    <Statistic
                        value={formatValue()}
                        prefix={configData?.isMoney ? '$' : undefined}
                        valueStyle={{
                            fontSize: '24px',
                            fontWeight: 700,
                            color: token.colorText,
                            lineHeight: 1.2
                        }}
                        formatter={(value) => {
                            if (typeof value === 'number') {
                                return value.toLocaleString();
                            }
                            return value;
                        }}
                    />
                </div>

                {/* 하단 링크 정보 */}
                <Text
                    type="secondary"
                    style={{
                        fontSize: '11px',
                        lineHeight: 1.3,
                        opacity: 0.8
                    }}
                >
                    {configData.link}
                </Text>

                {/* 선택 상태 하단 바 */}
                {isSelected && (
                    <div style={{
                        position: 'absolute',
                        bottom: '0',
                        left: '0',
                        right: '0',
                        height: '3px',
                        background: '#698cf3',
                        transition: 'all 0.3s ease'
                    }}/>
                )}
            </Card>
        </Tooltip>
    );
};

export default Widget;